import type { ReactElement, ReactNode } from 'react';
import type {
  TooltipProps as AriaTooltipProps,
  TooltipTriggerComponentProps,
} from 'react-aria-components';
import { Tooltip as TooltipComponent, TooltipTrigger } from 'react-aria-components';
import cx from 'clsx';
import { type BaseButtonProps, Button } from '#components/button/button';
import styles from './tooltip.module.css';

export interface TooltipProps extends TooltipTriggerComponentProps {
  children: ReactNode;
  label: string | ReactElement | ReactNode;
  placement?: AriaTooltipProps['placement'];
  className?: string;
  tooltipTestId?: string; // Custom test ID for the tooltip
  needsButton?: boolean;
  /**
   * Set to `true` when the `needsButton` trigger already has visible text (e.g. a labelled
   * `<Button>` or text content). The trigger then derives its accessible name from that text, so
   * the component skips deriving an `aria-label` from `label` (which would be redundant). Leave
   * unset for icon-only triggers, which need the derived label.
   */
  triggerHasVisibleText?: boolean;
  isDisabled?: boolean;
  buttonProps?: Omit<BaseButtonProps, 'children'>;
  offset?: number;
  crossOffset?: number;
}

export function Tooltip({
  children,
  label,
  placement = 'bottom',
  delay = 0,
  closeDelay = 0,
  className,
  tooltipTestId,
  needsButton = false,
  triggerHasVisibleText = false,
  isDisabled = false,
  offset = 8,
  crossOffset,
  buttonProps,
  ...triggerProps
}: TooltipProps): JSX.Element {
  // react-aria modal escapes the test container, we render it where it can find the tooltip (i.e. <div> component with 'ember-testing' id)
  const TEST_PORTAL_CONTAINER = document.getElementById('ember-testing') ?? undefined;

  // An icon-only `needsButton` trigger has no visible text, so the button needs an aria-label for
  // screen readers. Derive it from the string `label`. Precedence: an explicit `buttonProps`
  // aria-label always wins; otherwise we derive one unless the trigger already has visible text
  // (`triggerHasVisibleText`), in which case a derived label would be redundant.
  let buttonAriaLabel: string | undefined;
  if (buttonProps && 'aria-label' in buttonProps) {
    buttonAriaLabel = buttonProps['aria-label'];
  } else if (triggerHasVisibleText || typeof label !== 'string') {
    buttonAriaLabel = undefined;
  } else {
    buttonAriaLabel = label;
  }

  return (
    <TooltipTrigger
      closeDelay={closeDelay}
      data-test-tooltip
      data-testid="tooltip"
      delay={delay}
      isDisabled={isDisabled}
      {...triggerProps}
    >
      {needsButton ? (
        <Button
          className={styles.button}
          data-test-tooltip-button={tooltipTestId}
          data-tooltip-wrapper={tooltipTestId}
          {...buttonProps}
          aria-label={buttonAriaLabel}
        >
          {children}
        </Button>
      ) : (
        children
      )}
      <TooltipComponent
        UNSTABLE_portalContainer={TEST_PORTAL_CONTAINER}
        className={cx(styles.content, 'body-standard', className)}
        crossOffset={crossOffset}
        data-test-tooltip-message
        data-testid={tooltipTestId}
        offset={offset}
        placement={placement}
      >
        {label}
      </TooltipComponent>
    </TooltipTrigger>
  );
}
